import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { globalSearch } from "../services/api";
import { Users, Receipt, FileCheck, FileMinus, Briefcase, Megaphone, Loader2, CalendarDays, Plus, ArrowRight, MapPin, Camera } from "lucide-react";
import { typeIcon, STATUS_META } from "../utils/eventVisuals";

const DOC_META = {
  invoice: { icon: Receipt, label: "Facture" },
  quote: { icon: FileCheck, label: "Devis" },
  credit: { icon: FileMinus, label: "Avoir" },
};

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "");
const fmtEuro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const QUICK_ACTIONS = [
  { label: "Nouvelle facture", icon: Plus, to: "/document/new/invoice" },
  { label: "Nouveau devis", icon: Plus, to: "/document/new/quote" },
  { label: "Voir le planning", icon: CalendarDays, to: "/employes/planning" },
  { label: "Clients", icon: Users, to: "/clients" },
  { label: "Communication", icon: Megaphone, to: "/communication" },
];

export const GlobalSearch = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) { setQuery(""); setResults(null); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    const q = query.trim();
    if (q.length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await globalSearch(q);
        setResults(res.data);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query]);

  const go = (to) => {
    onOpenChange(false);
    navigate(to);
  };

  const hasResults = results && (
    results.customers?.length || results.documents?.length || results.events?.length ||
    results.employees?.length || results.subscriptions?.length
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 overflow-hidden max-w-xl gap-0 top-[18%] translate-y-0" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Recherche globale</DialogTitle>
        <Command shouldFilter={false} className="bg-transparent">
          <CommandInput
            placeholder="Rechercher un client, une facture, un événement, un employé…"
            value={query}
            onValueChange={setQuery}
            data-testid="global-search-input"
          />
          <CommandList className="max-h-[420px]" data-testid="global-search-results">
            {loading && (
              <div className="flex items-center justify-center py-6 text-slate-400">
                <Loader2 size={18} className="animate-spin" />
              </div>
            )}
            {!loading && query.trim().length >= 2 && !hasResults && (
              <CommandEmpty>Aucun résultat pour « {query} »</CommandEmpty>
            )}
            {query.trim().length < 2 && (
              <CommandGroup heading="Actions rapides">
                {QUICK_ACTIONS.map((a) => (
                  <CommandItem key={a.to + a.label} onSelect={() => go(a.to)} className="gap-2.5 cursor-pointer" data-testid={`search-quick-${a.to.replace(/\//g, "-")}`}>
                    <a.icon size={15} className="text-slate-400" />
                    <span className="flex-1">{a.label}</span>
                    <ArrowRight size={13} className="text-slate-300" />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {!loading && results?.customers?.length > 0 && (
              <CommandGroup heading="Clients">
                {results.customers.map((c) => {
                  const name = c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
                  return (
                    <CommandItem key={c.id} onSelect={() => go(`/reports/client/${c.id}`)} className="gap-2.5 cursor-pointer" data-testid={`search-customer-${c.id}`}>
                      <Users size={15} className="text-slate-400" />
                      <span className="flex-1 truncate">{name}</span>
                      {c.email && <span className="text-xs text-slate-400 truncate max-w-[180px]">{c.email}</span>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {!loading && results?.documents?.length > 0 && (
              <CommandGroup heading="Documents">
                {results.documents.map((d) => {
                  const meta = DOC_META[d.doc_type] || DOC_META.invoice;
                  const Icon = meta.icon;
                  return (
                    <CommandItem key={d.id} onSelect={() => go(`/document/${d.id}`)} className="gap-2.5 cursor-pointer" data-testid={`search-document-${d.id}`}>
                      <Icon size={15} className="text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <span className="block truncate">{meta.label} {d.number || "(brouillon)"} · {d.customer_name}</span>
                        {d.subject && <span className="block text-xs text-slate-400 truncate">{d.subject}</span>}
                      </div>
                      <span className="text-xs text-slate-500 font-numbers whitespace-nowrap">{fmtEuro(d.total_ttc)}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {!loading && results?.events?.length > 0 && (
              <CommandGroup heading="Événements">
                {results.events.map((ev) => {
                  const Icon = typeIcon(ev.event_type);
                  const st = STATUS_META[ev.status] || STATUS_META.todo;
                  return (
                    <CommandItem key={ev.id} onSelect={() => go(`/employes/planning?date=${ev.date}`)} className="gap-2.5 cursor-pointer" data-testid={`search-event-${ev.id}`}>
                      <span className="w-5 h-5 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: ev.color }}>
                        <Icon size={11} className="text-white" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="block truncate">{ev.title}</span>
                        <span className="block text-xs text-slate-400 truncate">
                          {fmtDate(ev.date)}{ev.start_time ? ` · ${ev.start_time}` : ""}{ev.location ? ` · ${ev.location}` : ""}
                        </span>
                      </div>
                      <span className="flex items-center gap-1 text-[10px] font-medium" style={{ color: st.color }}>
                        <st.icon size={11} /> {st.label}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {!loading && results?.employees?.length > 0 && (
              <CommandGroup heading="Employés">
                {results.employees.map((e) => (
                  <CommandItem key={e.id} onSelect={() => go(`/employes/${e.id}`)} className="gap-2.5 cursor-pointer" data-testid={`search-employee-${e.id}`}>
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0" style={{ backgroundColor: e.color || "#26788A" }}>
                      {(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}
                    </span>
                    <span className="flex-1 truncate">{e.first_name} {e.last_name}</span>
                    {e.position && <span className="text-xs text-slate-400 truncate max-w-[160px]">{e.position}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {!loading && results?.subscriptions?.length > 0 && (
              <CommandGroup heading="Abonnements communication">
                {results.subscriptions.map((s) => (
                  <CommandItem key={s.id} onSelect={() => go("/communication")} className="gap-2.5 cursor-pointer" data-testid={`search-subscription-${s.id}`}>
                    <Megaphone size={15} className="text-slate-400" />
                    <span className="flex-1 truncate">{s.customer_name}</span>
                    <span className="text-xs text-slate-400 uppercase">{s.plan}</span>
                    <span className="text-xs text-slate-500 font-numbers">{fmtEuro(s.monthly_price_ht)}/mois</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {!loading && results?.equipment?.length > 0 && (
              <CommandGroup heading="Matériel">
                {results.equipment.map((eq) => (
                  <CommandItem key={eq.id} onSelect={() => go("/materiel")} className="gap-2.5 cursor-pointer" data-testid={`search-equipment-${eq.id}`}>
                    <Camera size={15} className="text-slate-400" />
                    <span className="flex-1 truncate">{eq.name}</span>
                    {eq.brand_model && <span className="text-xs text-slate-400 truncate max-w-[140px]">{eq.brand_model}</span>}
                    <span className="text-xs text-slate-500 font-numbers">× {eq.quantity}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
